import React, { useEffect, useState, Suspense } from 'react';
import useQuestionBankContext from '../../../api/QuestionBankContext';
import Headline from '../../../components/ui/Headline';
import { useLoadingContext } from '../../../context/LoadingContext';
import { enhanceChartData } from '../../../features/stats/utils/statisticalCalculations';
import MainSection from '../../../layout/app/dashboard/MainSection';
import type { Category } from '../../../data/interface-question';

// Enhanced feature components - lazy loaded for performance
// eslint-disable-next-line @typescript-eslint/promise-function-async
const PerformanceSummary = React.lazy(() => import('../../../features/stats/components/PerformanceOverview/PerformanceSummary'));
// eslint-disable-next-line @typescript-eslint/promise-function-async
const SmartRecommendations = React.lazy(() => import('../../../features/stats/components/ActionableRecommendations/SmartRecommendations'));
// eslint-disable-next-line @typescript-eslint/promise-function-async
const CategoryScoreBreakdown = React.lazy(() => import('../../../features/stats/components/CategoryBreakdown/CategoryScoreBreakdown'));
// eslint-disable-next-line @typescript-eslint/promise-function-async
const EnhancedLineChart = React.lazy(() => import('../../../features/stats/components/TrendAnalysis/EnhancedLineChart'));
// eslint-disable-next-line @typescript-eslint/promise-function-async
const TrendAnalysis = React.lazy(() => import('../../../features/stats/components/TrendAnalysis/TrendAnalysis'));

// eslint-disable-next-line @typescript-eslint/promise-function-async
const SimpleBarChart = React.lazy(() => import('../../../features/stats/components/CategoryBreakdown/SimpleBarChart'));
// eslint-disable-next-line @typescript-eslint/promise-function-async
const SimpleVerticalBarChart = React.lazy(() => import('../../../features/stats/components/CategoryBreakdown/SimpleVerticalBarChart'));

// Loading component for Suspense
const LoadingCard = React.memo(() => (
	<div className="animate-pulse">
		<div className="bg-gray-200 rounded-lg h-32" />
	</div>
));
LoadingCard.displayName = 'LoadingCard';

// Removed unused ChartData interface - cleaned up code

const StatsDashboard = () => {
	const { setLoading, setLoadingMessage } = useLoadingContext();
	const { questions, categories, testRecords } = useQuestionBankContext();
	const [categoryObjs, setCategoryWithStats] = useState<Category[]>();
	const [chartView, setChartView] = useState<'horizontal' | 'vertical'>('horizontal');
	const [recordLimit, setRecordLimit] = useState<number | 'all'>(10);

	// Filter out diagnostic evaluations from all data sources
	const filteredTestRecords = React.useMemo(() => {
		return testRecords?.filter(record => 
			record.testLabel !== 'evaluacion-1' && 
			record.testLabel !== 'evaluacion-2'
		) ?? [];
	}, [testRecords]);

	const filteredQuestions = React.useMemo(() => {
		return questions?.filter(question => 
			!question.isPruebaDiagnostica &&
			question.source !== 'evaluacion-1' && 
			question.source !== 'evaluacion-2'
		) ?? [];
	}, [questions]);

	const filteredCategories = React.useMemo(() => {
		return categories?.filter(category => 
			!category.name.toLowerCase().includes('evaluación diagnóstica')
		) ?? [];
	}, [categories]);

	// Enhanced chart data with better processing
	const enhancedChartData = React.useMemo(() => {
		if (filteredTestRecords == null || filteredTestRecords.length === 0) return [];

		// Apply record limit based on user selection
		const recentRecords = recordLimit === 'all' 
			? filteredTestRecords 
			: filteredTestRecords.slice(0, recordLimit);
		return enhanceChartData(recentRecords);
	}, [filteredTestRecords, recordLimit]);

	const getTotalQuestionsByCategories = React.useCallback((categoryID: string): number => {
		if (filteredQuestions == null || filteredQuestions.length === 0) return 0;
		const count = filteredQuestions.filter((q) => {
			return q.categoryID === categoryID;
		}).length;
		return count;
	}, [filteredQuestions]);

	const countCorrectQuestionsByCategory = React.useCallback(async (
		categoryID: string
	): Promise<number[]> => {
		return await new Promise((resolve, reject) => {
			try {
				let correctAnswersInCategory = 0;
				let totalQuestionsInCategory = 0;
				filteredTestRecords?.forEach((record) => {
					const tempSum = record.questions?.reduce((sum, qRecord) => {
						const question = filteredQuestions?.find((q) => q.id === qRecord.id);

						if (question?.categoryID === categoryID) {
							totalQuestionsInCategory = totalQuestionsInCategory + 1;
							if (question?.answer === qRecord.selectedAnswer) {
								return sum + 1;
							}
						}

						return sum;
					}, 0);
					correctAnswersInCategory = (tempSum ?? 0) + correctAnswersInCategory;
				});
				resolve([correctAnswersInCategory, totalQuestionsInCategory]);
			} catch (error) {
				console.error('Error: ', error);
				reject(error);
			}
		});
	}, [filteredTestRecords, filteredQuestions]);

	const getTotals = React.useCallback(async (): Promise<void> => {
		return await new Promise((resolve) => {
			if (
				filteredQuestions == null ||
				filteredQuestions.length === 0 ||
				filteredCategories == null
			) {
				resolve();
				return;
			}
			setLoadingMessage('Analizando tus estadísticas...');
			setLoading(true);

			const newCategories: Category[] = [...filteredCategories];
			let completedCategories = 0;

			newCategories.forEach((category) => {
				category.totalQuestions = getTotalQuestionsByCategories(category.id);
				countCorrectQuestionsByCategory(category.id)
					.then((result: number[]) => {
						category.totalAnsweredCorrectly = result[0];
						category.totalAnsweredInQuestionRecords = result[1];
						completedCategories++;

						// Only stop loading when all categories are processed
						if (completedCategories === newCategories.length) {
							setLoading(false);
							resolve();
						}
					})
					.catch((error) => {
						console.error('Error: ', error);
						completedCategories++;
						if (completedCategories === newCategories.length) {
							setLoading(false);
							resolve();
						}
					});
			});

			setCategoryWithStats(newCategories);
		});
	}, [filteredQuestions, filteredCategories, getTotalQuestionsByCategories, countCorrectQuestionsByCategory, setLoading, setLoadingMessage]);

	useEffect(() => {
		// Smooth scroll to top of the page - accessibility improvement
		window.scrollTo({ top: 0, behavior: 'smooth' });

		getTotals().then(() => {
			setLoading(false);
		}).catch((err: unknown) => console.error('Error: ', err));
	}, [getTotals, setLoading]);

	return (
		<MainSection
			subTitle='Análisis completo de tu progreso académico con insights accionables para optimizar tu preparación.'
			title='Estadísticas de Rendimiento'>
			<div className='my-8 w-full px-4 sm:px-6 lg:px-8'>

				{/* Performance Overview Section */}
				<section aria-labelledby="overview-heading" className="mb-8">
					<h2 className="text-xl font-semibold text-gray-900 mb-4" id="overview-heading">
						Resumen de Rendimiento
					</h2>
					<Suspense fallback={<LoadingCard />}>
						<PerformanceSummary testRecords={filteredTestRecords ?? []} />
					</Suspense>
				</section>

				{/* Smart Recommendations Section */}
				<section aria-labelledby="recommendations-heading" className="mb-8">
					<h2 className="text-xl font-semibold text-gray-900 mb-4" id="recommendations-heading">
						Recomendaciones Personalizadas
					</h2>
					<Suspense fallback={<LoadingCard />}>
						<SmartRecommendations
							categories={categoryObjs ?? []}
							testRecords={filteredTestRecords ?? []}
						/>
					</Suspense>
				</section>

				{/* Category Score Breakdown Section */}
				<Suspense fallback={<LoadingCard />}>
					<CategoryScoreBreakdown categories={categoryObjs ?? []} />
				</Suspense>

				{/* Category Answer Breakdown Section */}
				<section aria-labelledby="answer-breakdown-heading" className="mb-8">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
						<h2 className="text-xl font-semibold text-gray-900 mt-0mb-2 sm:mb-0" id="answer-breakdown-heading">
							Desglose de Respuestas por Categoría
						</h2>
						
						{/* View Toggle */}
						<div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
							<button
								onClick={() => setChartView('horizontal')}
								className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
									chartView === 'horizontal'
										? 'bg-white text-gray-900 shadow-sm'
										: 'text-gray-600 hover:text-gray-900'
								}`}
								aria-pressed={chartView === 'horizontal'}
							>
								📊 Horizontal
							</button>
							<button
								onClick={() => setChartView('vertical')}
								className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
									chartView === 'vertical'
										? 'bg-white text-gray-900 shadow-sm'
										: 'text-gray-600 hover:text-gray-900'
								}`}
								aria-pressed={chartView === 'vertical'}
							>
								📈 Vertical
							</button>
						</div>
					</div>

					<p className="mb-6 text-gray-600">
						Visualización detallada de respuestas correctas e incorrectas en cada área de conocimiento.
						{chartView === 'vertical' && ' La vista vertical incluye estadísticas resumidas adicionales.'}
					</p>

					<Suspense fallback={<LoadingCard />}>
						{chartView === 'horizontal' ? (
							<SimpleBarChart
								categories={categoryObjs ?? []}
								testRecords={filteredTestRecords ?? []}
								questions={filteredQuestions ?? []}
							/>
						) : (
							<SimpleVerticalBarChart
								categories={categoryObjs ?? []}
								testRecords={filteredTestRecords ?? []}
								questions={filteredQuestions ?? []}
							/>
						)}
					</Suspense>
				</section>

				{/* Enhanced Charts Section */}
				<section aria-labelledby="trends-heading" className="mb-8">
					<h2 className="text-xl font-semibold text-gray-900 mb-4" id="trends-heading">
						Análisis de Tendencias
					</h2>
					<p className="mb-6 text-gray-600">
						Visualiza tu progreso a lo largo del tiempo con herramientas interactivas
						que te ayudan a identificar patrones en tu rendimiento.
					</p>

					<div className="space-y-8">
						{/* Enhanced Line Chart */}
						<div className="bg-white p-6 rounded-lg shadow-sm border">
							<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
															<h3 className="text-lg font-medium text-gray-900 mb-4 sm:mb-0">
								Evolución de Puntuaciones {recordLimit === 'all' 
									? `(Todos los Exámenes: ${filteredTestRecords?.length ?? 0})`
									: filteredTestRecords !== null && filteredTestRecords.length < recordLimit
										? `(Últimos ${filteredTestRecords.length} de ${recordLimit} Exámenes)`
										: `(Últimos ${recordLimit} Exámenes)`
								}
							</h3>
								
								{/* Record Limit Selection */}
								<div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
									{[10, 25, 50, 100].map((limit) => (
										<button
											key={limit}
											onClick={() => setRecordLimit(limit)}
											className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
												recordLimit === limit
													? 'bg-white text-gray-900 shadow-sm'
													: 'text-gray-600 hover:text-gray-900'
											}`}
											aria-pressed={recordLimit === limit}
										>
											{limit}
										</button>
									))}
									<button
										onClick={() => setRecordLimit('all')}
										className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
											recordLimit === 'all'
												? 'bg-white text-gray-900 shadow-sm'
												: 'text-gray-600 hover:text-gray-900'
										}`}
										aria-pressed={recordLimit === 'all'}
									>
										Todos
									</button>
								</div>
							</div>
							<Suspense fallback={<LoadingCard />}>
								<EnhancedLineChart
									data={enhancedChartData}
									height={400}
									showBrush={enhancedChartData.length > 5}
								/>
							</Suspense>
						</div>

						{/* Trend Analysis */}
						<Suspense fallback={<LoadingCard />}>
							<TrendAnalysis testRecords={filteredTestRecords ?? []} />
						</Suspense>
					</div>
				</section>



				{/* Future Features Teaser */}
				<section className="mb-4" id='future-features'>
					<Headline title='Coming Soon' type='h3' />
					<div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
						<div className="flex items-start space-x-4">
							{/* <div aria-hidden="true" className="text-3xl">🚀</div> */}
							<div>
								<h4 className="font-medium text-blue-900 mt-0 mb-2">Mejoras en desarrollo por venir</h4>
								<ul className="text-sm text-blue-800 space-y-1">
									<li>• Análisis predictivo de preparación para exámenes</li>
									<li>• Comparación con otros estudiantes (anónima)</li>
									<li>• Recomendaciones de horarios de estudio personalizados</li>
									<li>• Seguimiento de tiempo de estudio por categoría</li>
									<li>• Exportación de reportes en PDF</li>
								</ul>
							</div>
						</div>
					</div>
				</section>
			</div>
		</MainSection>
	);
};

export default StatsDashboard;
